// views/Config/GradeGeracaoAnual.tsx
//
// Grade de 12 meses de uma usina num ano: o que a coleta apurou, o que foi
// corrigido a mao e a meta do laudo, lado a lado.
//
// Existe porque a automacao nem sempre fecha o mes. O caso que motivou: os
// blocos de Morrinhos so passaram a ter telemetria em 05/03/2026 e as Qgbt em
// 21/08 — o backfill do fabricante cobriu, mas ele proprio pode faltar (a Qgbt2
// nao tem abril/2026 na API). Quando nenhuma das duas fontes fecha, alguem
// precisa poder digitar o numero da fatura da distribuidora e seguir a vida.
//
// ⚠️ O ajuste manual vence sobre TUDO — telemetria propria, backfill do
// fabricante e ate o dia marcado prioritario. Por isso exige motivo: seis meses
// depois, ninguem lembra por que aquele mes tem um numero diferente do que a
// API devolve, e a Analise mostra a procedencia.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Loader2, Save, RotateCcw, TriangleAlert, CheckCircle2, PencilLine, Info,
} from 'lucide-react';
import { api } from '../../src/services/api';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import type { UnidadeGeradora, GradeGeracaoMes } from '../../types';
import { getDisplayUsina } from '../Geradoras/geradorasShared';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const fmt = (v: number | null): string =>
    v == null ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });

const pct = (real: number | null, meta: number | null): number | null =>
    real == null || !meta ? null : (real / meta) * 100;

/** Aceita "180.341", "180341,5" e "180341". Vazio => null (limpar o ajuste). */
const parseKwh = (v: string): number | null => {
    const t = v.trim();
    if (!t) return null;
    const n = Number(t.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : NaN;
};

interface Props {
    unidades: UnidadeGeradora[];
    site: string;
    onTrocarSite: (s: string) => void;
}

interface Rascunho { valor: string; motivo: string }

const GradeGeracaoAnual: React.FC<Props> = ({ unidades, site, onTrocarSite }) => {
    const { user } = useAuth();
    const { addToast } = useToast();

    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [linhas, setLinhas] = useState<GradeGeracaoMes[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState<number | null>(null);
    // Só os meses que a pessoa tocou. Sem isto, salvar mandaria 12 upserts e
    // carimbaria autor/data em mês que ninguém editou.
    const [rascunhos, setRascunhos] = useState<Record<number, Rascunho>>({});

    const carregar = useCallback(async () => {
        if (!site) return;
        setCarregando(true);
        try {
            setLinhas(await api.geradoras.getGradeAnual(site, ano));
            setRascunhos({});
        } catch (e: any) {
            addToast(e?.message || 'Falha ao carregar a grade.', 'error');
        } finally {
            setCarregando(false);
        }
    }, [site, ano, addToast]);

    useEffect(() => { carregar(); }, [carregar]);

    const anos = useMemo(() => {
        const lista: number[] = [];
        for (let a = anoAtual + 1; a >= 2025; a--) lista.push(a);
        return lista;
    }, [anoAtual]);

    const totalEfetivo = linhas.reduce((s, l) => s + (l.efetivo_kwh ?? 0), 0);
    const totalMeta = linhas.reduce((s, l) => s + (l.meta_kwh ?? 0), 0);

    const salvarMes = async (l: GradeGeracaoMes) => {
        const r = rascunhos[l.mes];
        if (!r) return;
        const valor = parseKwh(r.valor);
        if (Number.isNaN(valor)) { addToast('Valor inválido.', 'error'); return; }

        setSalvando(l.mes);
        try {
            if (valor == null) {
                await api.geradoras.removerAjusteMensal(site, ano, l.mes);
                addToast(`${MESES[l.mes - 1]} voltou para o valor automático.`, 'success');
            } else {
                if (!r.motivo.trim()) {
                    addToast('Escreva o motivo: o ajuste sobrepõe a medição.', 'error');
                    setSalvando(null);
                    return;
                }
                await api.geradoras.salvarAjusteMensal(site, ano, l.mes, valor, r.motivo.trim(), user?.id);
                addToast(`${MESES[l.mes - 1]} ajustado para ${fmt(valor)} kWh.`, 'success');
            }
            await carregar();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao salvar o ajuste.', 'error');
        } finally {
            setSalvando(null);
        }
    };

    const editar = (mes: number, campo: keyof Rascunho, v: string, atual: GradeGeracaoMes) =>
        setRascunhos(r => ({
            ...r,
            [mes]: {
                valor: campo === 'valor' ? v : (r[mes]?.valor ?? (atual.ajuste_kwh != null ? String(atual.ajuste_kwh) : '')),
                motivo: campo === 'motivo' ? v : (r[mes]?.motivo ?? atual.motivo ?? ''),
            },
        }));

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[16rem]">
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1">
                        Usina
                    </label>
                    <select
                        value={site}
                        onChange={e => onTrocarSite(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    >
                        {unidades.map(u => {
                            const d = getDisplayUsina(u);
                            return <option key={u.site_id_api} value={u.site_id_api}>{d.marca} · {d.uc}</option>;
                        })}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-1">
                        Ano
                    </label>
                    <select
                        value={ano}
                        onChange={e => setAno(Number(e.target.value))}
                        className="px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white tabular-nums"
                    >
                        {anos.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <button
                    onClick={carregar}
                    className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 inline-flex items-center gap-2"
                >
                    <RotateCcw size={14} /> Recarregar
                </button>
            </div>

            <div className="rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/15 px-4 py-3 flex items-start gap-2">
                <Info size={15} className="text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                <p className="text-xs text-gray-700 dark:text-gray-300">
                    <b>Automático</b> é o que a coleta apurou (telemetria de 5 min + histórico do fabricante).
                    Só preencha o <b>ajuste</b> quando esse número estiver errado — ele sobrepõe a medição
                    em toda a plataforma, então precisa de motivo.
                </p>
            </div>

            {carregando ? (
                <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
                    <Loader2 size={18} className="animate-spin mr-2" /> Carregando...
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs min-w-[54rem]">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60">
                                    <th className="py-2.5 px-3 font-bold">Mês</th>
                                    <th className="py-2.5 px-3 font-bold text-right">Automático (kWh)</th>
                                    <th className="py-2.5 px-3 font-bold text-right">Ajuste manual</th>
                                    <th className="py-2.5 px-3 font-bold">Motivo do ajuste</th>
                                    <th className="py-2.5 px-3 font-bold text-right">Meta</th>
                                    <th className="py-2.5 px-3 font-bold text-right">%</th>
                                    <th className="py-2.5 px-3 font-bold"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
                                {linhas.map(l => {
                                    const r = rascunhos[l.mes];
                                    const sujo = !!r;
                                    const p = pct(l.efetivo_kwh, l.meta_kwh);
                                    return (
                                        <tr key={l.mes} className={l.ajuste_kwh != null ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}>
                                            <td className="py-2 px-3 font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                                {MESES[l.mes - 1]}
                                                <span className="block text-[9px] font-normal text-gray-400">
                                                    {l.dias_com_dado > 0
                                                        ? `${l.dias_com_dado} de ${l.dias_no_mes} dias com leitura`
                                                        : 'sem leitura'}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                                                {fmt(l.automatico_kwh)}
                                                {l.automatico_kwh != null && !l.completo && (
                                                    <span title="Mês incompleto: fica fora do percentual da Análise até ser ajustado.">
                                                        <TriangleAlert size={11} className="inline ml-1 text-amber-500" />
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-2 px-3">
                                                <input
                                                    type="text" inputMode="decimal"
                                                    placeholder="—"
                                                    value={r?.valor ?? (l.ajuste_kwh != null ? String(l.ajuste_kwh) : '')}
                                                    onChange={e => editar(l.mes, 'valor', e.target.value, l)}
                                                    className="w-28 px-2 py-1 text-xs text-right tabular-nums rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                                />
                                            </td>
                                            <td className="py-2 px-3">
                                                <input
                                                    type="text"
                                                    placeholder={l.ajuste_kwh != null || r?.valor ? 'obrigatório' : ''}
                                                    value={r?.motivo ?? l.motivo ?? ''}
                                                    onChange={e => editar(l.mes, 'motivo', e.target.value, l)}
                                                    className="w-full min-w-[12rem] px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                                />
                                            </td>
                                            <td className="py-2 px-3 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmt(l.meta_kwh)}</td>
                                            <td className={`py-2 px-3 text-right tabular-nums font-bold ${p == null ? 'text-gray-400'
                                                : p >= 95 ? 'text-emerald-600 dark:text-emerald-400'
                                                    : p >= 85 ? 'text-amber-600 dark:text-amber-400'
                                                        : 'text-rose-600 dark:text-rose-400'}`}>
                                                {p == null ? '—' : `${p.toFixed(0)}%`}
                                            </td>
                                            <td className="py-2 px-3">
                                                {sujo ? (
                                                    <button
                                                        onClick={() => salvarMes(l)}
                                                        disabled={salvando === l.mes}
                                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary text-white text-[11px] font-bold hover:bg-primary/90 disabled:opacity-50"
                                                    >
                                                        {salvando === l.mes
                                                            ? <Loader2 size={11} className="animate-spin" />
                                                            : <Save size={11} />}
                                                        Salvar
                                                    </button>
                                                ) : l.ajuste_kwh != null ? (
                                                    <span title="Valor corrigido à mão" className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400">
                                                        <PencilLine size={11} /> ajustado
                                                    </span>
                                                ) : l.automatico_kwh != null ? (
                                                    <CheckCircle2 size={12} className="text-emerald-500" />
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60 font-bold">
                                    <td className="py-2.5 px-3 text-gray-800 dark:text-gray-200">Ano {ano}</td>
                                    <td className="py-2.5 px-3 text-right tabular-nums text-gray-900 dark:text-white" colSpan={3}>
                                        {fmt(totalEfetivo)} kWh
                                    </td>
                                    <td className="py-2.5 px-3 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmt(totalMeta)}</td>
                                    <td className="py-2.5 px-3 text-right tabular-nums text-gray-900 dark:text-white">
                                        {totalMeta > 0 ? `${((totalEfetivo / totalMeta) * 100).toFixed(0)}%` : '—'}
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Deixe o campo de ajuste <b>vazio</b> e salve para voltar ao valor automático daquele mês.
                A meta vem do laudo PVsyst e é a mesma em todo ano — edite na aba <b>Laudo PVsyst</b>.
            </p>
        </div>
    );
};

export default GradeGeracaoAnual;
